import { useSponsorships } from "@/api/sponsorships";
import { useState, useMemo } from "react";
import {
  Plus,
  Banknote,
  Download,
  Award,
  ChevronDown,
  ChevronUp,
  Receipt,
  Phone,
  Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { currencies } from "@/lib/constants";
import { RecordPaymentModal } from "@/components/sponsorship/RecordPaymentModal";
import { CertificateModal } from "@/components/sponsorship/CertificateModal";
import { PaymentReceiptModal } from "@/components/payment/PaymentReceiptModal";
import { AttributionBadge } from "@/components/followup/AttributionBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AddMahalluSponsorshipForm } from "./components/add-mahallu-form";
import { EditSponsorshipModal } from "@/components/sponsorship/EditSponsorshipModal";
import { SearchInput } from "@/components/ui/search-input";

/* ── Table Row ── */
function SponsorshipRow({
  sponsorship,
  onRecordPayment,
  onCertificate,
  onReceipt,
  onEdit,
}) {
  const sym = currencies[sponsorship.currency || "INR"]?.symbol || "₹";
  const remaining = sponsorship.amountPromised - sponsorship.paidAmount;
  const pct =
    sponsorship.amountPromised > 0
      ? Math.min(sponsorship.paidAmount / sponsorship.amountPromised, 1)
      : 0;
  const [expanded, setExpanded] = useState(false);

  const statusClass =
    sponsorship.status === "completed"
      ? "bg-emerald-100 text-emerald-700"
      : sponsorship.status === "active"
        ? "bg-blue-100 text-blue-700"
        : "bg-amber-100 text-amber-700";
  const statusLabel =
    sponsorship.status === "completed"
      ? "Paid"
      : sponsorship.status === "active"
        ? "Partial"
        : "Pending";

  const payments = sponsorship.payments || [];

  return (
    <div className="border-b last:border-b-0">
      {/* Main Row */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 hover:bg-muted/40 transition-colors">
        {/* Expand toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => setExpanded(!expanded)}
          disabled={payments.length === 0}
        >
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </Button>

        {/* ID */}
        <span className="text-xs text-muted-foreground font-mono w-16 shrink-0 hidden lg:block">
          {sponsorship.sponsorshipId}
        </span>

        {/* Sponsor */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {sponsorship.sponsorName}
          </p>
          {sponsorship.sponsorPhone && (
            <p className="text-[10px] text-muted-foreground truncate flex items-center gap-0.5 mt-0.5">
              <Phone className="h-2.5 w-2.5" />
              {sponsorship.sponsorPhone}
            </p>
          )}
          <p className="text-xs text-muted-foreground truncate">
            {sponsorship.mahalluName}
            {sponsorship.careOf?.name
              ? ` · c/o ${sponsorship.careOf.name}`
              : ""}
          </p>
        </div>

        {/* Pledged */}
        <div className="text-right w-20 sm:w-24 shrink-0">
          <p className="text-sm font-semibold tabular-nums">
            {sym}
            {sponsorship.amountPromised?.toLocaleString()}
          </p>
          <p className="text-[10px] text-muted-foreground hidden sm:block">
            Sponsored
          </p>
        </div>

        {/* Paid */}
        <div className="text-right w-20 sm:w-24 shrink-0">
          <p className="text-sm font-bold tabular-nums text-emerald-600">
            {sym}
            {sponsorship.paidAmount?.toLocaleString()}
          </p>
          <p className="text-[10px] text-muted-foreground hidden sm:block">
            Collected
          </p>
        </div>

        {/* Remaining */}
        <div className="text-right w-20 sm:w-24 shrink-0 hidden md:block">
          <p
            className={cn(
              "text-sm font-semibold tabular-nums",
              remaining > 0 ? "text-amber-600" : "text-muted-foreground",
            )}
          >
            {sym}
            {remaining.toLocaleString()}
          </p>
          <p className="text-[10px] text-muted-foreground">Balance</p>
        </div>

        {/* Progress */}
        <div className="w-16 sm:w-20 shrink-0 hidden sm:block">
          <div className="flex items-center gap-1.5">
            <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  pct >= 1
                    ? "bg-emerald-500"
                    : pct > 0
                      ? "bg-primary"
                      : "bg-muted-foreground/20",
                )}
                style={{ width: `${pct * 100}%` }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums w-7">
              {Math.round(pct * 100)}%
            </span>
          </div>
        </div>

        {/* Status */}
        <span
          className={cn(
            "text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0",
            statusClass,
          )}
        >
          {statusLabel}
        </span>

        {/* Date */}
        <div className="w-24 text-xs text-muted-foreground shrink-0 text-right hidden lg:block">
          {new Date(sponsorship.createdAt).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 shrink-0">
          {/* {remaining > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => onRecordPayment(sponsorship)}
                >
                  <Banknote className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Record Payment</TooltipContent>
            </Tooltip>
          )} */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-blue-600 hover:bg-blue-50"
                onClick={() => onEdit(sponsorship)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Edit</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-violet-600 hover:bg-violet-50"
                onClick={() => onCertificate(sponsorship)}
              >
                <Award className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Certificate</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Expanded Payment History */}
      {expanded && payments.length > 0 && (
        <div className="bg-muted/30 border-t">
          <div className="px-4 sm:px-6 py-2 flex items-center gap-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">
            <span className="w-24">Payment ID</span>
            <span className="flex-1">Date</span>
            <span className="w-24 text-right">Amount</span>
            <span className="w-8"></span>
          </div>
          {payments
            .slice()
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((p) => {
              const pSym = sym;
              return (
                <div
                  key={p._id}
                  className="flex items-center gap-3 px-4 sm:px-6 py-2 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-b-0"
                >
                  <span className="text-xs font-mono text-muted-foreground w-24 truncate">
                    #{p.paymentId}
                  </span>
                  <span className="text-xs text-muted-foreground flex-1">
                    {new Date(p.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                    {" · "}
                    {new Date(p.createdAt).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </span>
                  <span className="flex flex-col items-end gap-0.5 w-24">
                    <span className="text-xs font-semibold text-emerald-600 tabular-nums">
                      +{pSym}
                      {p.amount?.toLocaleString()}
                    </span>
                    {p.isFollowUpAttributed && <AttributionBadge />}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          onReceipt({
                            paymentId: p.paymentId,
                            name: sponsorship.sponsorName,
                            place:
                              sponsorship.mahalluName ||
                              sponsorship.place ||
                              "",
                            amount: p.amount,
                            currency: sponsorship.currency || "INR",
                            createdAt: p.createdAt,
                            isFollowUpAttributed: p.isFollowUpAttributed,
                          })
                        }
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Download Receipt
                    </TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default function Mahallu() {
  const { data: sponsorships, isLoading } = useSponsorships();
  const [activeFilter, setActiveFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [payTarget, setPayTarget] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [certTarget, setCertTarget] = useState(null);
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  // Only Mahallu type
  const mahalluSponsorships = useMemo(
    () => (sponsorships || []).filter((sp) => sp.type === "Mahallu"),
    [sponsorships],
  );

  // Unique mahallu names for pills
  const mahalluNames = useMemo(() => {
    const names = [
      ...new Set(
        mahalluSponsorships.map((sp) => sp.mahalluName).filter(Boolean),
      ),
    ];
    return ["All", ...names.sort()];
  }, [mahalluSponsorships]);

  // Filtered list
  const filtered = useMemo(() => {
    let filtered = mahalluSponsorships;
    if (activeFilter !== "All") {
      filtered = filtered.filter((sp) => sp.mahalluName === activeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (sp) =>
          sp.sponsorName?.toLowerCase().includes(q) ||
          sp.sponsorPhone?.toLowerCase().includes(q) ||
          sp.mahalluName?.toLowerCase().includes(q) ||
          sp.careOf?.name?.toLowerCase().includes(q),
      );
    }
    return filtered;
  }, [mahalluSponsorships, activeFilter, search]);

  // Summary stats for filtered
  const summary = useMemo(() => {
    const pledged = filtered.reduce((s, sp) => s + (sp.amountPromised || 0), 0);
    const collected = filtered.reduce((s, sp) => s + (sp.paidAmount || 0), 0);
    return {
      pledged,
      collected,
      remaining: pledged - collected,
      count: filtered.length,
    };
  }, [filtered]);

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-10 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-16 rounded-full" />
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-8 w-20 rounded-full" />
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="px-4 pt-3 pb-1 flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-tight">Mahallu</h1>
            <p className="text-xs text-muted-foreground">
              Mahallu sponsorships · {summary.count} sponsors
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setShowAdd(true)}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>

        {/* Filter pills */}
        <ScrollArea className="w-full">
          <div className="flex gap-2 px-4 py-2">
            {mahalluNames.map((name) => (
              <Button
                key={name}
                variant={activeFilter === name ? "default" : "outline"}
                size="sm"
                className="rounded-full shrink-0 h-7 text-xs px-3"
                onClick={() => setActiveFilter(name)}
              >
                {name}
              </Button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Search */}
        <div className="px-4 py-2">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, mahallu, or care of..."
          />
        </div>
      </div>

      {/* ── Summary Bar ── */}
      <div className="px-4 py-2.5 flex items-center gap-4 sm:gap-6 border-b bg-muted/30 text-xs">
        <div>
          <span className="text-muted-foreground">Total Sponsored:</span>{" "}
          <span className="font-bold tabular-nums">
            ₹{summary.pledged.toLocaleString()}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Collected:</span>{" "}
          <span className="font-bold tabular-nums text-emerald-600">
            ₹{summary.collected.toLocaleString()}
          </span>
        </div>
        <div className="hidden sm:block">
          <span className="text-muted-foreground">Balance:</span>{" "}
          <span className="font-bold tabular-nums text-amber-600">
            ₹{summary.remaining.toLocaleString()}
          </span>
        </div>
      </div>

      {/* ── Table Header ── */}
      <div className="px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b bg-muted/20">
        <div className="h-7 w-7 shrink-0" />
        <span className="w-16 shrink-0 hidden lg:block">ID</span>
        <span className="flex-1 min-w-0">Sponsor</span>
        <span className="w-20 sm:w-24 text-right shrink-0">Sponsored</span>
        <span className="w-20 sm:w-24 text-right shrink-0">Collected</span>
        <span className="w-20 sm:w-24 text-right shrink-0 hidden md:block">
          Balance
        </span>
        <span className="w-16 sm:w-20 text-center shrink-0 hidden sm:block">
          Progress
        </span>
        <span className="w-12 text-center shrink-0">Status</span>
        <span className="w-24 text-center shrink-0 hidden lg:block">Date</span>
        <span className="w-16 sm:w-20 shrink-0 text-center">Actions</span>
      </div>

      {/* ── Rows ── */}
      <div className="flex-1">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Receipt className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              {search || activeFilter !== "All"
                ? "No matching sponsors"
                : "No mahallu sponsors found"}
            </p>
          </div>
        ) : (
          <Card className="mx-3 sm:mx-4 my-3 overflow-hidden">
            <CardContent className="p-0">
              <TooltipProvider delayDuration={300}>
                {filtered.map((sp) => (
                  <SponsorshipRow
                    key={sp._id}
                    sponsorship={sp}
                    onRecordPayment={setPayTarget}
                    onCertificate={setCertTarget}
                    onReceipt={setReceiptTarget}
                    onEdit={setEditTarget}
                  />
                ))}
              </TooltipProvider>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modals */}
      {payTarget && (
        <RecordPaymentModal
          isOpen={!!payTarget}
          onClose={() => setPayTarget(null)}
          sponsorship={payTarget}
        />
      )}

      {certTarget && (
        <CertificateModal
          isOpen={!!certTarget}
          onClose={() => setCertTarget(null)}
          sponsorship={certTarget}
        />
      )}

      {receiptTarget && (
        <PaymentReceiptModal
          isOpen={!!receiptTarget}
          onClose={() => setReceiptTarget(null)}
          payment={receiptTarget}
        />
      )}

      <EditSponsorshipModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        sponsorship={editTarget}
      />

      {/* Add Mahallu Sponsorship Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Mahallu Sponsorship</DialogTitle>
            <DialogDescription>
              Add a new mahallu sponsorship sponsor
            </DialogDescription>
          </DialogHeader>
          <AddMahalluSponsorshipForm onSuccess={() => setShowAdd(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
